import React, { useState } from 'react';

/**
 * Stop-the-timer dialog. Arrives PRE-FILLED with the timer's `suggested` text —
 * computed server-side as coalesce(note typed at start, task title, thread
 * subject) and returned identically by fn_timer_start and fn_my_timesheet, so the
 * pre-fill never depends on whether the timer was just started or the page was
 * reloaded. Never re-derive it here. Actions are `[Discard] [Save]`.
 *
 * A timer running more than 8 hours is SURFACED for correction, never silently
 * accepted. The threshold lives in the database: `running.over_8h` is computed
 * once server-side and passed straight through — the elapsed clock is for display
 * only and must not recompute the rule. `over_8h` is deliberately NOT
 * `needs_review`: on a completed entry needs_review means the reaper voided an
 * abandoned timer, which is a different fact and must not share a field name.
 *
 * @param {object}  props
 * @param {string}  [props.suggested]  Server-computed pre-fill for the description.
 * @param {boolean} [props.overEight]  Server flag — `running.over_8h`.
 * @param {(description:string, reason:string|null)=>void} props.onSave
 * @param {()=>void} props.onClose
 * @param {boolean} [props.saving]
 * @param {string}  [props.error]
 */
export default function StopTimerDialog({
  suggested = '',
  overEight = false,
  onSave,
  onClose,
  saving = false,
  error,
}) {
  const [description, setDescription] = useState(suggested || '');
  // fn_timer_stop does not require a description, but §2 does: the pre-fill means
  // Save is almost never empty, and a person who deletes the suggestion should
  // have to say what they did instead. So Save is gated on a non-empty value.
  const canSave = description.trim().length > 0 && !saving;

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 p-0 md:p-4" role="dialog" aria-modal="true" aria-label="Stop timer">
      <div className="w-full md:max-w-lg bg-white dark:bg-gray-900 rounded-t-2xl md:rounded-2xl shadow-xl border dark:border-gray-800 p-5">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Stop timer</h2>

        {overEight && (
          <div
            className="mt-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-800 dark:text-amber-300"
            data-testid="over-8h-note"
          >
            This entry is over 8 hours — verify or correct the time before saving.
          </div>
        )}

        <label className="block mt-4 text-sm font-medium text-gray-700 dark:text-gray-300">
          What did you work on?
        </label>
        <textarea
          data-testid="stop-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          className="mt-1 w-full text-sm rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 p-2"
          placeholder="In your own words…"
        />

        {error && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-700 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            Discard
          </button>
          <button
            type="button"
            onClick={() => onSave?.(description.trim(), null)}
            disabled={!canSave}
            data-testid="stop-save"
            className="text-sm font-medium rounded-lg bg-[#32B1FF] hover:bg-[#1a9ce6] text-white px-4 py-2 disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
